import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, type ApiError, type ListLeadsResult } from '@whinerp/client';
import { EmptyState, ErrorState, Num, Skeleton, type Column } from '@whinerp/ui';
import { formatCount, formatRelativeDate } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { MqlQueuePanel } from './mql-queue-panel.js';
import { RecordList } from '../shared/record-list.tsx';
import { ViewBar } from '../shared/view-bar.js';
import { nextSort, useSavedView, visibleColumns } from '../shared/use-saved-view.js';

/**
 * SC-09 Leads.
 *
 * The inbound queue, worked by status and score. Sorted by score then created
 * date, which is the order a rep should work it in rather than alphabetically -
 * a lead list sorted by name is a list nobody works top to bottom.
 *
 * The status chips are derived from the workspace's own statuses with counts,
 * so a workspace that adds "Nurturing" gets a chip for it without anybody
 * editing this screen.
 */

type Row = ListLeadsResult['rows'][number];

function LeadsListScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [sort, setSort] = useState<{ field: string; direction: 'asc' | 'desc' }>({
    field: 'score',
    direction: 'desc',
  });
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack[cursorStack.length - 1] ?? null;

  // The saved view, its unsaved draft and what a request has to send: the same
  // answer the contacts list uses.
  const savedView = useSavedView(() => setCursorStack([]));

  /*
   * Opening the queue is what makes the response clock mean anything.
   *
   * There is no scheduler in this product, so the sweep that reports leads
   * nobody has touched runs here - which is exactly when it is most use, and
   * why each breach is claimed with one conditional UPDATE rather than
   * counted. A ref rather than a query because this WRITES, and React's
   * development double-mount would otherwise fire it twice.
   *
   * Failure is deliberately silent: somebody opening the leads list came to
   * work leads, and a banner about a background sweep would sit on top of the
   * work.
   */
  const swept = useRef(false);
  const escalate = useMutation({ mutationFn: () => api.escalateLeads({}) });
  const sweep = escalate.mutate;
  useEffect(() => {
    if (swept.current) return;
    swept.current = true;
    sweep();
  }, [sweep]);

  const query = useQuery<ListLeadsResult, ApiError>({
    queryKey: ['leads', { search, status, sort, cursor, view: savedView.params }],
    queryFn: () =>
      api.listLeads({
        query: {
          ...(search.trim() ? { search: search.trim() } : {}),
          ...(status ? { status } : {}),
          ...(cursor ? { cursor } : {}),
          ...savedView.params,
          // The queue's column headers used to move an arrow and reorder
          // nothing: the sort was state the request never carried.
          sort: sort.field,
          direction: sort.direction,
          limit: 50,
        },
      }),
  });

  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        key: 'name',
        header: t('object.lead'),
        sortField: 'name',
        // The column the row IS. Simple never drops it.
        essential: true,
        cell: (row) => row.fullName || t('state.notSet'),
      },
      {
        key: 'company',
        header: t('object.account'),
        sortField: 'company',
        cell: (row) => row.companyName ?? '',
      },
      {
        key: 'score',
        header: t('lead.score'),
        sortField: 'score',
        align: 'end',
        cell: (row) => (
          <Num align="end" className={row.score >= 40 ? 'text-ink' : 'text-ink-meta'}>
            {formatCount(row.score, format)}
          </Num>
        ),
      },
      {
        key: 'status',
        header: t('lead.status'),
        // The workspace's own word for it. An enum is never shown raw.
        cell: (row) => row.statusLabel ?? row.status,
      },
      {
        key: 'source',
        header: t('lead.source'),
        cell: (row) => row.source ?? '',
        hideOnCard: true,
      },
      {
        key: 'created',
        header: t('lead.created'),
        sortField: 'created',
        cell: (row) => <Num className="text-ink-meta">{formatRelativeDate(row.createdAt, format)}</Num>,
        hideOnCard: true,
      },
    ],
    [t, format],
  );

  const statuses = query.data?.statuses ?? [];
  const visible = useMemo(
    () => visibleColumns(columns, query.data?.columns ?? null),
    [columns, query.data?.columns],
  );

  /*
   * What a group heading says, per groupable field.
   *
   * Written out rather than indexed off the row, because the API field name
   * and the row property are deliberately different - `company` is
   * `companyName` - and a lookup by string would be another place the two
   * vocabularies have to agree.
   */
  const groupLabel: Record<string, (row: Row) => string | null> = {
    status: (row) => row.statusLabel ?? row.status,
    company: (row) => row.companyName,
    owner: (row) => row.ownerName ?? null,
  };
  const groupBy = query.data?.groupBy ?? null;
  const grouping = groupBy ? groupLabel[groupBy] : undefined;

  // The groups the server says it will honour, labelled here: it decides what
  // works, the screen decides what it is called.
  const groupNames: Record<string, string> = {
    status: t('lead.status'),
    company: t('object.account'),
    owner: 'Owner',
  };
  const groupable = (query.data?.groupable ?? [])
    .filter((field) => Object.hasOwn(groupNames, field))
    .map((field) => ({ field, label: groupNames[field]! }));

  return (
    <div className="flex w-full flex-col gap-4">
      {/* What marketing handed over and sales owes an answer on (F-078). */}
      <MqlQueuePanel />
    <RecordList<Row>
      title={t('object.lead.plural')}
      icon="UserPlus"
      rowId={(row) => row.leadId}
      recordHref={(row) => `/leads/${row.leadId}`}
      columns={visible}
      {...(grouping ? { group: { label: grouping, emptyLabel: t('view.group.empty') } } : {})}
      viewBar={
        <ViewBar
          objectKey="lead"
          active={savedView.active}
          draft={savedView.draft}
          columns={columns.map((column) => ({ key: column.key, label: column.header }))}
          groupable={groupable}
          onChange={savedView.onChange}
          onSelect={savedView.onSelect}
          onRemoveFilter={savedView.onRemoveFilter}
          onDiscard={savedView.onDiscard}
        />
      }
      page={query.data ? { ...query.data, rows: query.data.rows } : undefined}
      loading={query.isPending}
      error={query.isError ? { message: query.error.message, rule: query.error.rule } : null}
      onRetry={() => void query.refetch()}
      refreshing={query.isFetching}
      search={search}
      onSearchChange={(value) => {
        setSearch(value);
        setCursorStack([]);
      }}
      sort={sort}
      onSortChange={(field) => {
        setSort((current) => nextSort(current, field));
        // A different order is a different page. The cursor names a position
        // in the order it was made in.
        setCursorStack([]);
      }}
      cursorStack={cursorStack}
      onCursorStackChange={setCursorStack}
      pageSize={50}
      filtered={search.trim().length > 0 || status !== null}
      onClearFilters={() => {
        setSearch('');
        setStatus(null);
        setCursorStack([]);
      }}
      filterChips={
        status
          ? [
              {
                key: 'status',
                label: statuses.find((s) => s.key === status)?.label ?? status,
                onRemove: () => setStatus(null),
              },
            ]
          : []
      }
      aboveTable={
        <div data-status-chips className="flex flex-wrap items-center gap-2 pb-2">
          {statuses.map((entry) => (
            <button
              key={entry.key}
              type="button"
              data-status-chip={entry.key}
              aria-pressed={status === entry.key}
              onClick={() => {
                setStatus(status === entry.key ? null : entry.key);
                setCursorStack([]);
              }}
              className={
                status === entry.key
                  ? 'inline-flex h-chip items-center gap-1 rounded-chip bg-bronze-tint px-2 text-meta text-bronze-text'
                  : 'inline-flex h-chip items-center gap-1 rounded-chip bg-sunken px-2 text-meta text-ink-600 hover:text-ink'
              }
            >
              {entry.label}
              {/* From count() per status, with every other filter applied but
                  not the status filter itself - so selecting one does not show
                  the rest as zero. */}
              <Num className="text-ink-meta">{formatCount(entry.count, format)}</Num>
            </button>
          ))}
        </div>
      }
      emptyIllustration="UserPlus"
      emptyTitle={t('lead.empty.title')}
      emptyBody={t('lead.empty.body')}
    />
    </div>
  );
}

function LeadsListEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="UserPlus"
      title={t('lead.empty.title')}
      body={t('lead.empty.body')}
      primary={{ label: t('empty.action.addLead'), icon: 'Plus', href: '/leads' }}
      secondary={{ label: t('empty.action.importContacts'), icon: 'Upload', href: '/setup/import' }}
    />
  );
}

function LeadsListLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex flex-col gap-2" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      {Array.from({ length: 8 }, (_, index) => (
        <Skeleton key={index} className="h-row-comfortable w-full" />
      ))}
    </div>
  );
}

function LeadsListError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="Leads could not be loaded"
      body={error instanceof Error ? error.message : 'The list did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-09',
  route: '/leads',
  surface: 'page',
  title: 'Leads',
  // F-026: the leads list with its status filter chips, which is what this
  // screen is. It used to claim F-036 - web-to-lead forms - which is not built.
  features: ['F-026', 'F-078'],
  entitlement: 'F-036',
  reachedBy: [{ kind: 'nav', item: 'leads' }],
  // ViewBar, not ViewSelector. There was one of each: the ViewBar reads and
  // writes saved views, and the ViewSelector above it offered a single option
  // called "All" with an onSelect that did nothing. One control per question.
  components: ['DataTable', 'FilterChips', 'ViewBar'],
  elements: ['RecordList'],
  states: {
    empty: LeadsListEmpty,
    loading: LeadsListLoading,
    error: LeadsListError,
  },
  component: LeadsListScreen,
});

export default screen;
